import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Formik } from "formik";
import ReactDOMServer from "react-dom/server";
import { AppDatePicker, AppMultiSelect, AppNoInfo, AppRemixIcon } from "@/layout/AppComponents";
import Translator from "../../../layout/components/helpers/Translator";
import DeleteModal from "../../../layout/components/Modal/DeleteModal";
import AppInput from "../../../layout/components/form/AppInput";
import BreadcrumbHandler from "../../../layout/components/breadcrumb/BreadcrumbHandler";
import { axiosPath } from "../../../routing/helpers/router";
import useIsMounted from "../../../layout/components/hooks/useIsMounted";

const IntelligenceStoredReportsScreen = () => {
  const { moduleBridgeHex } = useParams();
  const isMounted = useIsMounted();
  const [module, setModule] = useState(null);
  const [filters, setFilters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRemoveReportModal, setShowRemoveReportModal] = useState(null);
  const [reloadData, setReloadData] = useState(null);

  // > LOAD MODULE
  const loadModule = useCallback(async () => {
    try {
      const response = await axios.post(
        axiosPath("api_r_user_module_intelligence_savedReports"),
        { moduleBridgeHex, filters }
      );
      return response.data;
    } catch (err) {}
  });

  useEffect(() => {
    setLoading(true);
    loadModule().then((response) => {
      if (isMounted.current) setModule(response);
      setLoading(false);
    });
  }, [moduleBridgeHex, filters, reloadData]);
  // < LOAD MODULE

  const handleSetFilters = (values) => {
    setFilters(values);
  };

  const handleGetReportFile = async (id) => {
    try {
      const response = await axios.post(
        axiosPath("api_r_user_module_intelligence_getSavedReport"),
        { moduleBridgeHex, intelligenceStoreID: id }
      );
      window.location.href = response.data.s3FileUrl;
    } catch (err) {}
  };

  const handleDeleteReportFile = async (id) => {
    try {
      await axios.post(
        axiosPath("api_r_user_module_intelligence_deleteSavedReport"),
        { moduleBridgeHex, intelligenceStoreID: id }
      );
      setShowRemoveReportModal(null);
      setReloadData(id);
    } catch (err) {}
  };

  if (!module) {
    if (loading) {
      return <AppNoInfo />;
    }
    return (
      <div className="mt-5 relative m_sr p-3">
        <div className="content-header"></div>
      </div>
    );
  }

  return (
    <div className="relative m_sr p-3">
      <div className="content-header">
        <BreadcrumbHandler
          paths={[
            {
              name: module.name,
              route: {
                name: "user_module_intelligence_home",
                params: { moduleBridgeHex },
              },
            },
            {
              name: ReactDOMServer.renderToString(
                <Translator file="module-intelligence" name="reports.store" />
              ),
            },
          ]}
        />
      </div>
      <section className="content">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <table className="table  table-bordered">
                  <thead>
                    <Formik
                      initialValues={{
                        from: null,
                        to: null,
                        comment: "",
                        reportOption: "",
                        reports: [],
                        userBridges: [],
                      }}
                      onSubmit={handleSetFilters}
                    >
                      {({ setFieldValue, handleSubmit, values, resetForm }) => (
                        <tr className="d-flex bg-gray-light">
                          <th className="col-lg-3">
                            <AppInput
                              title={ReactDOMServer.renderToString(
                                <Translator
                                  file="module-intelligence"
                                  name="find.by.description"
                                />
                              )}
                              icon={<AppRemixIcon icon="sound-module" />}
                              value={values.comment}
                              onChange={(v) => setFieldValue("comment", v)}
                              isClearable
                            />
                          </th>
                          <th className="col-lg-2">
                            <AppInput
                              title={ReactDOMServer.renderToString(
                                <Translator
                                  file="module-intelligence"
                                  name="find.by.report.option"
                                />
                              )}
                              icon={<AppRemixIcon icon="sound-module" />}
                              value={values.reportOption}
                              onChange={(v) => setFieldValue("reportOption", v)}
                              isClearable
                            />
                          </th>
                          <th className="col-lg-2">
                            <div className="row">
                              <div className="col-lg-12">
                                <AppDatePicker
                                  title={<Translator
                                    file="module-lawFinder"
                                    name="module.lf.filter.from"
                                  />}
                                  value={values.from}
                                  onChange={(date) =>
                                    setFieldValue("from", date)
                                  }
                                  isClearable
                                />
                              </div>
                              <div className="col-lg-12">
                                <AppDatePicker
                                  title={<Translator
                                    file="module-lawFinder"
                                    name="module.lf.filter.to"
                                  />}
                                  value={values.to}
                                  onChange={(date) => setFieldValue("to", date)}
                                  isClearable
                                />
                              </div>
                            </div>
                          </th>
                          <th className="col-lg-2">
                            {module.reports && (
                              <AppMultiSelect
                                options={module.reports}
                                onChange={(e) => setFieldValue("reports", e)}
                                title={ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-intelligence"
                                    name="reports"
                                  />
                                )}
                                icon={<AppRemixIcon icon="file" />}
                                className="form-control mb-3"
                                value={values.reports}
                                isMulti
                                hasSearchBox
                                hasSelectOptions
                                isClearable
                              />
                            )}
                          </th>
                          <th className="col-lg-2">
                            {module.userBridges && (
                              <AppMultiSelect
                                options={module.userBridges}
                                onChange={(e) =>
                                  setFieldValue("userBridges", e)
                                }
                                title={ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-intelligence"
                                    name="users"
                                  />
                                )}
                                icon={<AppRemixIcon icon="user" />}
                                className="form-control mb-3"
                                value={values.userBridges}
                                isMulti
                                hasSearchBox
                                hasSelectOptions
                                isClearable
                              />
                            )}
                          </th>
                          <th className="col-lg-1">
                            <div className="row">
                              <div className="col-lg-6">
                                <span
                                  className="btn btn-info btn-block btn-sm"
                                  onClick={handleSubmit}
                                >
                                  <AppRemixIcon icon="filter" />
                                </span>
                              </div>
                              <div className="col-lg-6">
                                <span
                                  className={`btn btn-danger btn-block btn-sm ${
                                    !filters && "disabled"
                                  }`}
                                  onClick={() => {
                                    if (filters) resetForm();
                                    setFilters(null);
                                  }}
                                >
                                  <AppRemixIcon icon="delete-bin" />
                                </span>
                              </div>
                            </div>
                          </th>
                        </tr>
                      )}
                    </Formik>
                    <tr className="d-flex">
                      <th className="col-lg-3">
                        <Translator
                          file="module-intelligence"
                          name="description"
                        />
                      </th>
                      <th className="col-lg-2">
                        <Translator
                          file="module-intelligence"
                          name="report.option"
                        />
                      </th>
                      <th className="text-lg-center col-lg-2">
                        <Translator file="module-intelligence" name="date" />
                      </th>
                      <th className="col-lg-2">
                        <Translator
                          file="module-intelligence"
                          name="module.int.report"
                        />
                      </th>
                      <th className="col-lg-2">
                        <Translator file="module-intelligence" name="user" />
                      </th>
                      <th className="text-lg-center col-lg-1">
                        <Translator file="user" name="user.download" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {!loading ? (
                      module.savedReports.map((sr, i) => (
                        <tr key={i} className="d-flex">
                          <td className="col-lg-3">
                            <span>{sr.comment}</span>
                          </td>
                          <td className="col-lg-2">{sr.reportOption}</td>
                          <td className="text-lg-center col-lg-2">{sr.date}</td>
                          <td className="col-lg-2">{sr.report}</td>
                          <td className="col-lg-2">
                            <b>{sr.user}</b>
                          </td>
                          <td className="col-lg-1">
                            <div
                              style={{
                                display: "flex",
                                flex: 1,
                                justifyContent: "space-evenly",
                              }}
                            >
                              <span
                                className="btn btn-success btn-sm"
                                onClick={() => handleGetReportFile(sr.id)}
                              >
                                <AppRemixIcon icon="file-excel" />
                              </span>
                              <span
                                className="btn btn-danger btn-sm"
                                onClick={() => setShowRemoveReportModal(sr.id)}
                              >
                                <AppRemixIcon icon="delete-bin" />
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6}>
                          <AppNoInfo />
                        </td>
                      </tr>
                    )}
                    {module.savedReports.length === 0 && (
                      <tr>
                        <td colSpan={6}>
                          <div className="alert alert-info">
                            <Translator
                              file="module-saicaReports"
                              name="no.results"
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>
      <DeleteModal
        show={showRemoveReportModal !== null}
        onConfirm={() => handleDeleteReportFile(showRemoveReportModal)}
        onClose={() => setShowRemoveReportModal(null)}
        title="Eliminar informe"
        text={
          <p>
            <Translator
              file="module-intelligence"
              name="confirm.delete.report.no.reversible"
              isHtml
            />
          </p>
        }
        sensitive
        sensitiveWord={ReactDOMServer.renderToString(
          <Translator file="general" name="general.delete" />
        )}
      />
    </div>
  );
};

export default IntelligenceStoredReportsScreen;
